<template>
  <QueryLoading v-if="isLoading" />
  <QueryError
    v-else-if="isError && data == null"
    class="h-64"
  >
    Failed to retrieve transaction report
  </QueryError>
  <div v-else-if="data" class="flex h-64 w-full flex-col">
    <template v-if="data">
      <BaseLineChart
        id="my-chart"
        :data="chartData"
        class="size-full overflow-hidden"
        @hover-datapoint="handleHoverDatapoint"
      />

      <div
        class="h-20 shrink-0 rounded-lg bg-background-fg/5 p-4"
      >
        <div class="text-sm text-background-fg/70">
          <template v-if="hoveredData">
            <template v-if="isDayGraph">
              {{ formatToHourRangeString(hoveredData) }}
            </template>
            <template v-else>
              {{ formatToDate(hoveredData.start) }}
            </template>
          </template>
          <template v-else>
            {{ dataDateRangeString }}
          </template>
        </div>
        <div class="text-xl font-semibold">
          <template v-if="hoveredData">
            {{
              formatToMerchantCurrencyString(
                hoveredData.total
              )
            }}
          </template>
          <template v-else>
            {{
              formatToMerchantCurrencyString(
                dataRangeCumulativeTotal
              )
            }}
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  type TransactionReportPeriod,
  useTransactionReport,
} from "@/services/reports/retrieveTransactionReport"
import { useCssVar } from "@vueuse/core"

const { formatToMerchantCurrencyString } = useCurrency()

const props = defineProps<{
  timePeriod: TransactionReportPeriod
}>()

const isDayGraph = computed(
  () =>
    props.timePeriod === "Today" ||
    props.timePeriod === "Yesterday" ||
    (typeof props.timePeriod === "object" &&
      props.timePeriod.date)
)
const { data, isError, isLoading } = useTransactionReport(
  computed(() => props.timePeriod)
)

const labels = computed(() => {
  if (!data.value) return []
  return isDayGraph.value
    ? [
        data.value[0].start,
        ...data.value.map(({ end }) => end),
      ]
    : [
        DateFns.addDays(data.value[0].start, -1),
        ...data.value.map(({ start }) => start),
      ]
})

const cumulativeDataset = computed(() => {
  if (!data.value) return []

  let mapReduced: number[] = [0]
  let cumulativeSum = 0
  for (const { total } of data.value) {
    cumulativeSum = cumulativeSum + total
    mapReduced = [...mapReduced, cumulativeSum]
  }
  return mapReduced
})

const colorPrimary = useCssVar("--color-primary")
const chartData = reactiveComputed(() => ({
  labels: labels.value,
  datasets: [
    {
      data: cumulativeDataset.value,
      pointRadius: 0,
      borderColor: `rgb(${colorPrimary.value})`,
    },
  ],
}))

const hoveredIndex = ref()
const handleHoverDatapoint = (
  index: number | undefined
) => {
  hoveredIndex.value =
    data.value &&
    index !== undefined &&
    index >= 1 &&
    index <= data.value?.length
      ? index - 1
      : undefined
}

const hoveredData = computed(() => {
  if (!data.value) return
  return hoveredIndex.value !== undefined
    ? {
        start: data.value[hoveredIndex.value].start,
        end: data.value[hoveredIndex.value].end,
        total: data.value[hoveredIndex.value].total,
        cumulative:
          cumulativeDataset.value[hoveredIndex.value + 1],
      }
    : undefined
})

const dataDateRangeString = computed(() => {
  if (!data.value) return
  const start = data.value[0].start

  const end = data.value[data.value.length - 1].start

  return isDayGraph.value
    ? formatToDate(start)
    : `${formatToDate(start)} - ${formatToDate(end)}`
})

const dataRangeCumulativeTotal = computed(
  () =>
    data.value?.reduce(
      (accumulator, { total }) => accumulator + total,
      0
    ) || 0
)

const formatToHourRangeString = ({
  start,
  end,
}: {
  start: Date
  end: Date
}) => `${formatToTime(start)} - ${formatToTime(end)}`
</script>
